import React, { useMemo } from 'react';

// Components
import { WpIcon } from 'Wpds/components/icon';
import { WpCollapsible } from 'Wpds/components/collapsible';
import { WpDivider } from 'Wpds/components/divider';
import WorkspaceDetailsListWrapper from './WorkspaceDetailsListWrapper';
import { WpAvatar } from 'Wpds/components/avatar';
import { WpText } from 'Wpds/components/text';
import UserBaProfileSnapshot from 'Shared/components/UserBaProfileSnapshot/UserBaProfileSnapshot';
import { WpDrawerV2, WpDrawerV2Body } from 'Wpds/components/drawer';
import { WpButton } from 'Wpds/components/button';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useBaProfile } from 'Shared/utils/hooks/use-ba-profile';
import { useTeamInfo } from 'Shared/utils/hooks/use-team-info';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useSmac } from 'Shared/utils/hooks/use-smac';

// Styles
import './TeamMemberWrapper.scss';

// Types
import { User } from 'Shared/constants/types';

// Constants
import ColorConstants from 'Wpds/colors/ColorConstants';

type Props = {
  user: User;
};

const TeamMemberWrapper: React.FC<Props> = ({ user }: Props) => {
  const baseClassName = 'TeamMemberWrapper';
  const [isOpen, toggleIsOpen] = useToggle(false);
  const [isDrawerOpen, toggleIsDrawerOpen] = useToggle(false);
  const { strengths, referencePatternName } = useBaProfile({ user });
  const { individualContributionInsight } = useTeamInfo();
  const { user: currentUser } = useCurrentUser();
  const { hasDesignAccess } = useSmac();

  // Find the user's strongest contribution from team info data if has Design access
  const strongestContribution = useMemo(() => {
    if (!hasDesignAccess) {
      return '';
    }

    if (!individualContributionInsight?.length) {
      return '';
    }

    // Find the insight matching this user's piUserGuid
    const userInsight = individualContributionInsight.find(insight => insight.surveyUserGuid === user.piUserGuid);
    return userInsight?.contributionInsight;
  }, [individualContributionInsight, user.piUserGuid]);

  return (
    <>
      <div className={baseClassName}>
        <div
          className={`${baseClassName}__collapsible`}
          role="button"
          onClick={toggleIsOpen}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleIsOpen();
            }
          }}
          tabIndex={0}
          aria-expanded={isOpen}
        >
          <div className={`${baseClassName}__collapsible__title`}>
            <WpAvatar
              avatar={user.avatar}
              referencePattern={referencePatternName}
              showReferencePatternIcon
              size="sm"
            ></WpAvatar>
            <div className={`${baseClassName}__collapsible__title__text`}>
              <WpText tag="span" variant="bodySm">
                {user.fullName}{' '}
                {user.piUserGuid === currentUser.piUserGuid ? (
                  <WpText tag="span" variant="bodySm" color="gray-600">
                    (You)
                  </WpText>
                ) : null}
              </WpText>
            </div>
          </div>
          <WpIcon name={isOpen ? 'chevron-up' : 'chevron-down'} />
        </div>

        <WpCollapsible open={isOpen}>
          <WpDivider padding={8} />

          <WorkspaceDetailsListWrapper
            title="Strengths"
            list={strengths}
            highlightedItemContent={strongestContribution}
            highlightedItemIcon="lightbulb-on"
            highlightedItemIconColor={ColorConstants.blue[500]}
            highlightedItemTitle="Strongest team contribution"
          />

          <div className={`${baseClassName}__collapsible__full-details-link`}>
            <WpButton variant="link" onClick={toggleIsDrawerOpen}>
              See full details
              <WpIcon name="long-arrow-right" />
            </WpButton>
          </div>
        </WpCollapsible>
        <WpDivider padding={8} />
      </div>
      <WpDrawerV2 isOpen={isDrawerOpen} toggleIsOpen={toggleIsDrawerOpen}>
        <WpDrawerV2Body>
          <UserBaProfileSnapshot user={user}></UserBaProfileSnapshot>
        </WpDrawerV2Body>
      </WpDrawerV2>
    </>
  );
};

export default TeamMemberWrapper;
